import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SkillList from './SkillList';
import AvailableSkills from './AvailableSkills';
import { ToastContainer, toast } from 'react-toastify';
import { calculateSpCost } from '../../utils/calculateSpCost';
import 'react-toastify/dist/ReactToastify.css';
import './CharacterSkills.css';
import './AvailableSkills.css';
import * as utils from './utils';
import * as api from './utils-api';
import { useCharacter } from "./CharacterProvider";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Touch-capable device? Drives whether we say "Long tap" vs "Right click".
const IS_TOUCH_DEVICE =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

// Normalized names of the two "slotted" groups. Within each group the character
// picks (implicitly) a Primary / Secondary / Tertiary slot; the slot is assigned
// automatically so the cheapest tier always goes to the most-purchased skill.
// NOTE: names must match the *normalized* (trimmed, lower-cased) skill name.
// "Rune Carving" normalizes to "rune carving" (with a space) — not "runecarving".
const GATHERING_SKILL_NAMES = ["mining", "foraging", "logging"];
const PRODUCTION_SKILL_NAMES = ["smithing", "alchemy", "rune carving"];

const normalizeName = (name) => (name || '').trim().toLowerCase();

// Pure cost engine: given the current skill list + the catalog of available
// skills + the character (for primary/secondary skillset), return a map of
// skillId -> SP cost. This is the single source of truth for every SP number in
// the UI. It does NOT read component state, so it can be safely called with the
// "next" character that is still being computed inside a state updater.
function computeSkillCosts(skills, availableSkills, character) {
    const result = {};
    const baseById = (id) => availableSkills.find(a => a.skillId === id);
    const nameOf = (s) => normalizeName(s.name || (baseById(s.skillId) || {}).name);

    // Slotted groups: sort members by ranks (desc), then skillId (asc) for a
    // stable tie-break, and assign Primary/Secondary/Tertiary tiers by position.
    const assignGroup = (groupNames) => {
        const members = skills
            .filter(s => groupNames.includes(nameOf(s)))
            .slice()
            .sort((a, b) => (Number(b.ranks) || 0) - (Number(a.ranks) || 0) || (a.skillId - b.skillId));

        members.forEach((s, idx) => {
            const base = baseById(s.skillId);
            if (!base) { result[s.skillId] = 0; return; }
            let baseCost, incrementCost;
            if (idx === 0) { baseCost = base.primaryBaseCost; incrementCost = base.primaryIncrementCost; }
            else if (idx === 1) { baseCost = base.secondaryBaseCost; incrementCost = base.secondaryIncrementCost; }
            else if (idx === 2) { baseCost = base.tertiaryBaseCost; incrementCost = base.tertiaryIncrementCost; }
            else { baseCost = 0; incrementCost = 0; }
            result[s.skillId] = calculateSpCost(baseCost, incrementCost, Number(s.ranks) || 0);
        });
    };
    assignGroup(GATHERING_SKILL_NAMES);
    assignGroup(PRODUCTION_SKILL_NAMES);

    // Everything else: single-purchase skills use the primary tier; tiered skills
    // pick their tier from the character's primary/secondary skillset.
    skills.forEach(s => {
        if (Object.prototype.hasOwnProperty.call(result, s.skillId)) return; // already costed (slotted)
        const base = baseById(s.skillId);
        if (!base) { result[s.skillId] = 0; return; }
        let baseCost, incrementCost;
        if (base.secondaryBaseCost == null) {
            baseCost = base.primaryBaseCost; incrementCost = base.primaryIncrementCost;
        } else if (Number(base.skillset) === Number(character?.skillsetPrimary)) {
            baseCost = base.primaryBaseCost; incrementCost = base.primaryIncrementCost;
        } else if (Number(base.skillset) === Number(character?.skillsetSecondary)) {
            baseCost = base.secondaryBaseCost; incrementCost = base.secondaryIncrementCost;
        } else {
            baseCost = base.tertiaryBaseCost; incrementCost = base.tertiaryIncrementCost;
        }
        result[s.skillId] = calculateSpCost(baseCost, incrementCost, Number(s.ranks) || 0);
    });

    return result;
}

const calculateBodyPoints = (skills) => {
    const toughnessSkill = skills.find(s =>
        Number(s.skillId) === 31 ||
        (s.name && s.name.trim().toLowerCase() === "toughness")
    );
    return toughnessSkill ? 10 + Number(toughnessSkill.ranks) * 5 : 10;
};

const calculateDexArmor = (skills) => {
    const skill = skills.find(s =>
        Number(s.skillId) === 2 ||
        (s.name && s.name.trim().toLowerCase() === "dexterity armor")
    );
    return skill ? 5 + Number(skill.ranks) * 5 : 0;
};

const calculateMaxArmor = (skills) => {
    const skill = skills.find(s =>
        Number(s.skillId) === 28 ||
        (s.name && s.name.trim().toLowerCase() === "armor training")
    );
    return skill ? 5 + Number(skill.ranks) * 5 : 0;
};

const updatePoolPoints = (skills, poolName, availableSkills) => {
    const baseSkill = availableSkills.find(s => s.name && s.name.trim().toLowerCase() === poolName.toLowerCase());
    if (!baseSkill || !baseSkill.skillId) return 0;
    const poolSkill = skills.find(s => s.skillId === baseSkill.skillId);
    return poolSkill ? Number(poolSkill.ranks) * 10 : 0;
};

function shallowEqualSkills(a = [], b = []) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const x = a[i], y = b[i];
        if (x.skillId !== y.skillId) return false;
        if (Number(x.ranks || 0) !== Number(y.ranks || 0)) return false;
        if (Number(x.cost || 0) !== Number(y.cost || 0)) return false;
        if (Number(x.baseCostUsed || 0) !== Number(y.baseCostUsed || 0)) return false;
        if (Number(x.incrementCostUsed || 0) !== Number(y.incrementCostUsed || 0)) return false;
    }
    return true;
}
function shallowEqualCharacter(a, b) {
    if (a === b) return true;
    const keys = [
        "bodyPoints","maxArmor","dexArmor",
        "agilityPoints","arcanePoints","naturePoints","spiritPoints",
        "staminaPoints","thaumaturgyPoints",
        "skillPointsSpent","skillPointsUnspent"
    ];
    for (const k of keys) if ((a?.[k] || 0) !== (b?.[k] || 0)) return false;
    return shallowEqualSkills(a?.skills, b?.skills);
}


const CharacterSkills = ({ token, onSelectCharacter }) => {
    const { currentCharacter, setCurrentCharacter } = useCharacter();
    const [skillsets, setSkillsets] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [saving, setSaving] = useState(false);

    const [activeSkillsetTab, setActiveSkillsetTab] = useState(null);
    const [showAddSkillModal, setShowAddSkillModal] = useState(false);

    api.setToken(token);

    useEffect(() => {
        (async function () {
            setSkillsets(await api.fetchSkillsets());
        })();
    }, [token]);

    useEffect(() => {
        (async function () {
            const fetchedSkills = await api.fetchSkills({characterId: currentCharacter?.characterId});
            setAvailableSkills(fetchedSkills);
            if (currentCharacter && fetchedSkills) {
                setCurrentCharacter(prev =>
                    updateCalculatedFields({
                        ...prev,
                        skills: utils.addStaticSkills(prev.skills, fetchedSkills)
                    })
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCharacter?.characterId, token]);

    const skillsetMap = skillsets.reduce((map, s) => {
        map[String(s.skillsetId)] = s.name;
        return map;
    }, { '999': 'Transform' });

    useEffect(() => {
        if (availableSkills.length > 0 && !activeSkillsetTab) {
            const grouped = availableSkills.reduce((groups, skill) => {
                const groupKey = String(skill.skillset);
                if (!groups[groupKey]) groups[groupKey] = [];
                groups[groupKey].push(skill);
                return groups;
            }, {});
            const groupKeys = Object.keys(grouped).sort((a, b) => {
                const nameA = (skillsetMap[a] || "").toUpperCase();
                const nameB = (skillsetMap[b] || "").toUpperCase();
                return nameA.localeCompare(nameB);
            });
            if (groupKeys.length > 0) setActiveSkillsetTab(groupKeys[0]);
        }
    }, [availableSkills, activeSkillsetTab, skillsetMap]);

    // Recompute ONLY when tiering inputs change, and only write if something changed
    useEffect(() => {
        if (!currentCharacter) return;
        setCurrentCharacter(prev => {
            const next = updateCalculatedFields(prev);
            return shallowEqualCharacter(prev, next) ? prev : next;
        });
    }, [currentCharacter?.skillsetPrimary, currentCharacter?.skillsetSecondary, availableSkills]);

    // Helper: Single-purchase skills have only Primary costs (no Secondary/Tertiary defined)
    const isSinglePurchaseSkill = (skillId) => {
        const s = availableSkills.find(as => as.skillId === skillId);
        // If there's no secondaryBaseCost defined, we treat it as single-purchase
        return !!(s && (s.secondaryBaseCost == null || typeof s.secondaryBaseCost === 'undefined'));
    };

    function updateCalculatedFields(character) {
        const skills = character.skills || [];

        // Cost every skill from the *passed-in* character (never component state),
        // so buying/selling computes against the new ranks, not the previous render.
        const costs = computeSkillCosts(skills, availableSkills, character);

        // Add computed props per skill for printing
        const skillsWithComputed = skills.map(s => {
            const { baseCost, incrementCost } = getSkillRate(s, availableSkills, character);
            const cost = costs[s.skillId] || 0;
            return { ...s, cost, baseCostUsed: baseCost, incrementCostUsed: incrementCost };
        });

        // Reuse the previous array if annotations didn’t actually change
        const skillsUnchanged = shallowEqualSkills(skills, skillsWithComputed);
        const finalSkills = skillsUnchanged ? skills : skillsWithComputed;

        const spent = skillsWithComputed.reduce((acc, skill) => acc + (skill.cost || 0), 0);

        const computedValues = {
            bodyPoints: calculateBodyPoints(skillsWithComputed),
            maxArmor: calculateMaxArmor(skillsWithComputed) || 0,
            dexArmor: calculateDexArmor(skillsWithComputed) || 0,
            agilityPoints: updatePoolPoints(skillsWithComputed, "Agility Points", availableSkills),
            arcanePoints: updatePoolPoints(skillsWithComputed, "Attunement: Arcane", availableSkills),
            naturePoints: updatePoolPoints(skillsWithComputed, "Attunement: Nature", availableSkills),
            spiritPoints: updatePoolPoints(skillsWithComputed, "Attunement: Spirit", availableSkills),
            staminaPoints: updatePoolPoints(skillsWithComputed, "Stamina Points", availableSkills),
            thaumaturgyPoints: updatePoolPoints(skillsWithComputed, "Attunement: Thaumaturgy", availableSkills),
            skillPointsSpent: spent,
            skillPointsUnspent: (character.skillPointsEffective || character.skillPointsTotal || 0) - spent
        };

        //return { ...character, ...computedValues, skills: skillsWithComputed };
        return { ...character, ...computedValues, skills: finalSkills };
    }


    // Which base+increment costs apply to *this* skill for the current character?
    // Used only for the per-skill annotations (baseCostUsed/incrementCostUsed).
    function getSkillRate(skill, availableSkills, character) {
        const baseSkill = availableSkills.find(as => as.skillId === skill.skillId);
        if (!baseSkill) return { baseCost: 0, incrementCost: 0 };

        const normalizedName = normalizeName(skill.name || baseSkill.name);
        const characterSkills = character?.skills || [];

        const isGather = GATHERING_SKILL_NAMES.includes(normalizedName);
        const isProduce = PRODUCTION_SKILL_NAMES.includes(normalizedName);

        // For slotted groups, assign Primary/Secondary/Tertiary by highest ranks
        if (isGather || isProduce) {
            const groupNames = isGather ? GATHERING_SKILL_NAMES : PRODUCTION_SKILL_NAMES;

            const filteredAndSorted = characterSkills
                .filter(s => groupNames.includes(normalizeName(s.name || (availableSkills.find(as => as.skillId === s.skillId) || {}).name)))
                .sort((a, b) => (b.ranks || 0) - (a.ranks || 0) || (a.skillId - b.skillId));

            const idx = filteredAndSorted.findIndex(s => s.skillId === skill.skillId);
            if (idx === 0) return {
                baseCost: baseSkill.primaryBaseCost ?? 0,
                incrementCost: baseSkill.primaryIncrementCost ?? 0
            };
            if (idx === 1) return {
                baseCost: baseSkill.secondaryBaseCost ?? 0,
                incrementCost: baseSkill.secondaryIncrementCost ?? 0
            };
            if (idx === 2) return {
                baseCost: baseSkill.tertiaryBaseCost ?? 0,
                incrementCost: baseSkill.tertiaryIncrementCost ?? 0
            };
            // not in top three → no cost
            return { baseCost: 0, incrementCost: 0 };
        }

        // Single-purchase skills: use primary tier
        if (baseSkill.secondaryBaseCost == null) {
            return {
                baseCost: baseSkill.primaryBaseCost ?? 0,
                incrementCost: baseSkill.primaryIncrementCost ?? 0
            };
        }

        // Otherwise pick tier by char's primary/secondary
        if (Number(baseSkill.skillset) === Number(character?.skillsetPrimary)) {
            return {
                baseCost: baseSkill.primaryBaseCost ?? 0,
                incrementCost: baseSkill.primaryIncrementCost ?? 0
            };
        } else if (Number(baseSkill.skillset) === Number(character?.skillsetSecondary)) {
            return {
                baseCost: baseSkill.secondaryBaseCost ?? 0,
                incrementCost: baseSkill.secondaryIncrementCost ?? 0
            };
        } else {
            return {
                baseCost: baseSkill.tertiaryBaseCost ?? 0,
                incrementCost: baseSkill.tertiaryIncrementCost ?? 0
            };
        }
    }

    // Build the effective skill list from the current character, with `skill`
    // forced to `ranks` (added if not yet owned, dropped if ranks <= 0).
    const withSkillRanks = (skill, ranks) => {
        const baseList = (currentCharacter?.skills || []).map(s => ({ ...s }));
        const idx = baseList.findIndex(s => s.skillId === skill.skillId);
        if (ranks <= 0) {
            return idx >= 0 ? baseList.filter(s => s.skillId !== skill.skillId) : baseList;
        }
        if (idx >= 0) {
            baseList[idx] = { ...baseList[idx], ranks };
        } else {
            const base = availableSkills.find(a => a.skillId === skill.skillId);
            baseList.push({ skillId: skill.skillId, name: skill.name || (base || {}).name, ranks });
        }
        return baseList;
    };

    // This skill's OWN SP cost at its current ranks (or `ranksOverride`), using
    // the same group-aware tiering as the stored total. Summing this over every
    // skill equals skillPointsSpent. Used for the per-card "(N SP)" display.
    const getSkillCost = (skill, ranksOverride) => {
        if (!currentCharacter) return 0;
        if (!availableSkills.find(s => s.skillId === skill.skillId)) return 0;
        const ranks = typeof ranksOverride === 'number'
            ? ranksOverride
            : (skill.ranks ? Number(skill.ranks) : 1);
        const costs = computeSkillCosts(withSkillRanks(skill, ranks), availableSkills, currentCharacter);
        return costs[skill.skillId] || 0;
    };

    // The SP delta of moving `skill` to `newRanks` — i.e. the real cost/refund of
    // the action, including any re-tiering it forces on the rest of the group.
    // (For non-slotted skills this reduces to the skill's own cost delta.)
    const getActionCost = (skill, newRanks) => {
        if (!currentCharacter) return 0;
        const totalAt = (ranks) => {
            const costs = computeSkillCosts(withSkillRanks(skill, ranks), availableSkills, currentCharacter);
            return Object.values(costs).reduce((sum, c) => sum + c, 0);
        };
        const current = Number(skill.ranks) || 0;
        return totalAt(newRanks) - totalAt(current);
    };

    const handleIncrementSkillRank = (skillId) => {
        // Disallow adding more than one rank for single-purchase skills
        /*if (isSinglePurchaseSkill(skillId)) {
            const existing = currentCharacter?.skills?.find(s => s.skillId === skillId);
            if (existing && Number(existing.ranks) >= 1) {
                const skill = availableSkills.find(s => s.skillId === skillId);
                toast.error(`${skill.name} can only be purchased once.`);
                return;
            }
        }*/
        setCurrentCharacter(prev => {
            const updatedSkills = prev.skills.map(skill =>
                skill.skillId === skillId ? { ...skill, ranks: skill.ranks + 1 } : skill
            );
            return updateCalculatedFields({
                ...prev,
                skills: updatedSkills
            });
        });
    };

    const handleDecrementSkillRank = (skillId) => {
        if (utils.isLockedSkill(availableSkills.find(s => s.skillId === skillId))) return; // locked
        console.log(`Decrementing skill ${skillId}`);
        setCurrentCharacter(prev => {
            const skill = prev.skills.find(s => s.skillId === skillId);
            if (!skill) return prev; // Shouldn't happen

            if (skill.ranks <= 1) {
                console.log(`Removing skill ${skill}`);
                // If only 1 rank, remove the skill entirely
                const updatedSkills = prev.skills.filter(s => s.skillId !== skillId);
                return updateCalculatedFields({
                    ...prev,
                    skills: updatedSkills
                });
            } else {
                // Otherwise, decrement
                const updatedSkills = prev.skills.map(s =>
                    s.skillId === skillId ? { ...s, ranks: s.ranks - 1 } : s
                );
                return updateCalculatedFields({
                    ...prev,
                    skills: updatedSkills
                });
            }
        });
    };

    const handleRemoveSkill = (skillId) => {
        if (utils.isLockedSkill(availableSkills.find(s => s.skillId === skillId))) return; // locked
        setCurrentCharacter(prev => {
            const updatedSkills = prev.skills.filter(s => s.skillId !== skillId);
            return updateCalculatedFields({
                ...prev,
                skills: updatedSkills
            });
        });
    };

    // Add skill (now only by double-click or button, not DnD)
    const handleDropSkill = skill => {
        if (
            skill.name.trim().toLowerCase() === "attunement: thaumaturgy" &&
            Number(currentCharacter.skillsetPrimary) !== Number(skill.skillset)
        ) {
            toast.error('You must select Thaumaturgy as your Primary Skillset to purchase Attunement: Thaumaturgy.');
            return;
        }
        if (currentCharacter.skills.find(s => s.skillId === skill.skillId)) {
            toast.info('Skill already added.');
            return;
        }
        const skillObj = {
            skillId: skill.skillId,
            ranks: 1,
            name: skill.name,
            prereqs: [],
            skillset: Number(skill.skillset) || 0,
        };
        setCurrentCharacter(prev => {
            const updatedCharacterSkills = [...(prev.skills || []), skillObj];
            return updateCalculatedFields({
                ...prev,
                skills: updatedCharacterSkills
            });
        });
        toast.success(`Added ${skill.name}`);
        setShowAddSkillModal(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updatedCharacter = {
                ...currentCharacter,
                race: currentCharacter.raceId
            };
            setCurrentCharacter(updatedCharacter);
            await axios.put(`${API_BASE_URL}/character/${currentCharacter.characterId}`, updatedCharacter, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await axios.get(`${API_BASE_URL}/character/${currentCharacter.characterId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Save successful!");
        } catch (error) {
            toast.error("Failed to save character");
        } finally {
            setTimeout(() => setSaving(false), 500);
        }
    };

    return (
        <div>
            {currentCharacter && (
                <div key={currentCharacter.characterId} className="character-edit-container fade-in">

                    {/* Mobile Add Skill Button */}
                    <div className="mobile-add-skill-btn-container">
                        <button
                            className="mobile-add-skill-btn"
                            onClick={() => setShowAddSkillModal(true)}
                        >
                            + Add Skill
                        </button>
                    </div>

                    <div className="skills-section">
                        {/* Current Skills Column */}
                        <div className="skills-column current-skills">
                            <div className="section-label">
                                Purchased Skills
                                <span className="section-subtitle">
                                    {IS_TOUCH_DEVICE ? 'Long tap' : 'Right click'} for description
                                </span>
                            </div>
                            <div className="current-skills-container">
                                {currentCharacter.skills && currentCharacter.skills.length > 0 ? (
                                    <SkillList
                                        availableSkills={availableSkills}
                                        skillsetMap={skillsetMap}
                                        getSkillCost={getSkillCost}
                                        getActionCost={getActionCost}
                                        onIncrement={handleIncrementSkillRank}
                                        onDecrement={handleDecrementSkillRank}
                                        onRemove={handleRemoveSkill}
                                    />
                                ) : (
                                    <p>No skills added.</p>
                                )}
                            </div>
                        </div>
                        {/* Available Skills Column (desktop only) */}
                        <div className="skills-column available-skills">
                            <div className="section-label">
                                Available Skills
                                <span className="section-subtitle">
                                    Click to add · {IS_TOUCH_DEVICE ? 'Long tap' : 'Right click'} for description
                                </span>
                            </div>
                            <AvailableSkills
                                availableSkills={availableSkills}
                                skillsetMap={skillsetMap}
                                activeSkillsetTab={activeSkillsetTab}
                                setActiveSkillsetTab={setActiveSkillsetTab}
                                currentCharacter={currentCharacter}
                                getSkillCost={getSkillCost}
                                getActionCost={getActionCost}
                                handleDropSkill={handleDropSkill}
                                onIncrement={handleIncrementSkillRank}
                            />
                        </div>
                    </div>
                    <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
                        {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} />

            {/* Mobile Add Skill Modal */}
            {showAddSkillModal && (
                <div className="mobile-skill-modal-overlay" onClick={() => setShowAddSkillModal(false)}>
                    <div
                        className="mobile-skill-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <span>Add a Skill</span>
                            <button
                                className="close-modal-btn"
                                onClick={() => setShowAddSkillModal(false)}
                            >✕</button>
                        </div>
                        <AvailableSkills
                            availableSkills={availableSkills}
                            skillsetMap={skillsetMap}
                            activeSkillsetTab={activeSkillsetTab}
                            setActiveSkillsetTab={setActiveSkillsetTab}
                            currentCharacter={currentCharacter}
                            getSkillCost={getSkillCost}
                            getActionCost={getActionCost}
                            handleDropSkill={handleDropSkill}
                            onIncrement={handleIncrementSkillRank}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CharacterSkills;
